import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { CampaignMembershipEntity } from './campaign-membership.entity';
import { CampaignEntity } from './campaign.entity';

export type CampaignMessageDirection = 'outbound' | 'inbound';

export type CampaignMessageStatus =
	| 'sent'
	| 'delivered'
	| 'opened'
	| 'clicked'
	| 'replied'
	| 'bounced'
	| 'failed';

/**
 * A single campaign message mirrored from the sending provider (an email sent to,
 * or received from, a contact). Full detail lives here; a curated subset is
 * reflected onto `client_activity` (via `fkClientActivityId`) so the touch shows
 * on the contact/account timeline alongside calls and notes.
 *
 * Not auditable: highest-volume, sync-written.
 */
@Index('campaign_message__external_uindex', ['externalMessageId'], {
	unique: true,
})
@Index('campaign_message__fk_membership', ['fkCampaignMembershipId'], {})
@Index('campaign_message__contact_sent', ['fkClientContactId', 'sentDate'], {})
@Entity('campaign_message', {}, { auditable: false })
export class CampaignMessageEntity extends BaseEntity {
	@Column('int', { name: 'fk_campaign__id', unsigned: true })
	public fkCampaignId!: number;

	@Column('int', { name: 'fk_campaign_membership__id', unsigned: true })
	public fkCampaignMembershipId!: number;

	@Column('int', {
		name: 'fk_client_contact__id',
		unsigned: true,
		nullable: true,
	})
	public fkClientContactId!: number | null;

	@Column('int', { name: 'fk_client__id', unsigned: true, nullable: true })
	public fkClientId!: number | null;

	@Column('varchar', {
		name: 'externalCampaignId',
		length: 255,
		nullable: true,
	})
	public externalCampaignId!: string | null;

	// The provider's message id (e.g. Instantly email id) — dedup key for the sync.
	@Column('varchar', { name: 'externalMessageId', length: 255, nullable: true })
	public externalMessageId!: string | null;

	@Column('varchar', { name: 'threadId', length: 255, nullable: true })
	public threadId!: string | null;

	@Column('varchar', { name: 'channel', length: 20, default: () => "'email'" })
	public channel!: string;

	@Column('varchar', { name: 'direction', length: 10 })
	public direction!: CampaignMessageDirection | string;

	// The sending mailbox the message went out on (Instantly `eaccount`).
	@Column('varchar', { name: 'mailboxEmail', length: 255, nullable: true })
	public mailboxEmail!: string | null;

	@Column('varchar', { name: 'fromEmail', length: 255, nullable: true })
	public fromEmail!: string | null;

	@Column('varchar', { name: 'toEmail', length: 255, nullable: true })
	public toEmail!: string | null;

	@Column('varchar', { name: 'subject', length: 500, nullable: true })
	public subject!: string | null;

	@Column('text', { name: 'bodyPreview', nullable: true })
	public bodyPreview!: string | null;

	@Column('longtext', { name: 'body', nullable: true })
	public body!: string | null;

	@Column('varchar', { name: 'status', length: 50, nullable: true })
	public status!: CampaignMessageStatus | string | null;

	@Column('datetime', { name: 'sentDate', nullable: true })
	public sentDate!: Date | null;

	// The client_activity timeline row this message created (the dovetail link).
	@Column('int', {
		name: 'fk_client_activity__id',
		unsigned: true,
		nullable: true,
	})
	public fkClientActivityId!: number | null;

	@Column('json', { name: 'metadata', nullable: true })
	public metadata!: Record<string, unknown> | null;

	@Column('datetime', { name: 'lastSyncedAt', nullable: true })
	public lastSyncedAt!: Date | null;

	@ManyToOne(() => CampaignEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_campaign__id', referencedColumnName: 'id' }])
	public fkCampaign!: CampaignEntity;

	@ManyToOne(() => CampaignMembershipEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_campaign_membership__id', referencedColumnName: 'id' },
	])
	public fkCampaignMembership!: CampaignMembershipEntity;
}
